import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { Button, Dialog, Field, FieldError, FieldLabel, Input, Select, Switch } from "@akuna/ui";
import { toast } from "@/hooks/use-toast";
import { useSaveModel } from "@/hooks/useCommissions";
import { commissionErrorKey } from "@/lib/commissions/api";
import type { CommissionModel } from "@/lib/commissions/types";

type Props = { open: boolean; onOpenChange: (open: boolean) => void; model?: CommissionModel | null; onSaved?: (id: string) => void };

export function NewModelDialog({ open, onOpenChange, model, onSaved }: Props) {
  const { t } = useTranslation("commissions");
  const save = useSaveModel();
  const schema = z.object({
    name: z.string().trim().min(1, t("catalog.models.dialog.nameRequired")).max(60),
    basis: z.enum(["gross", "after_discount"]),
    isActive: z.boolean(),
  });
  type Form = z.infer<typeof schema>;
  const form = useForm<Form>({ resolver: zodResolver(schema), defaultValues: { name: "", basis: "after_discount", isActive: true } });

  useEffect(() => {
    if (open) form.reset({ name: model?.name ?? "", basis: model?.calculation_basis ?? "after_discount", isActive: model?.is_active ?? true });
  }, [open, model, form]);

  const onSubmit = form.handleSubmit(async (v) => {
    try {
      const id = await save.mutateAsync({ id: model?.id ?? null, name: v.name, basis: v.basis, isActive: v.isActive });
      onSaved?.(id);
      toast({ title: t("catalog.models.dialog.saved") });
      onOpenChange(false);
    } catch (error) {
      toast({ title: t(commissionErrorKey(error)), variant: "destructive" });
    }
  });

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={model ? t("catalog.models.dialog.titleEdit") : t("catalog.models.dialog.titleNew")}
    >
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <Field invalid={!!form.formState.errors.name}>
          <FieldLabel>{t("catalog.models.dialog.name")}</FieldLabel>
          <Input {...form.register("name")} placeholder={t("catalog.models.dialog.namePlaceholder")} autoFocus />
          {form.formState.errors.name && <FieldError>{form.formState.errors.name.message}</FieldError>}
        </Field>
        <Controller control={form.control} name="basis" render={({ field }) => (
          <div className="flex flex-col gap-1.5">
            <Select
              forceNative
              label={t("basis.label")}
              value={field.value}
              onValueChange={field.onChange}
              options={(["after_discount", "gross"] as const).map((b) => ({ value: b, label: t(`basis.${b}`) }))}
            />
            <p className="text-xs text-muted-foreground">{t("basis.hint")}</p>
          </div>
        )} />
        <Controller control={form.control} name="isActive" render={({ field }) => (
          <Switch label={t("catalog.models.dialog.active")} checked={field.value} onCheckedChange={field.onChange} />
        )} />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t("catalog.models.dialog.cancel")}</Button>
          <Button type="submit" disabled={save.isPending}>{t("catalog.models.dialog.save")}</Button>
        </div>
      </form>
    </Dialog>
  );
}
